/**
 * useWorkspaceHelpers Hook
 * 
 * Provides helper functions for workspace settings:
 * - Date formatting
 * - Role display names
 * - Permission checks
 * - Email validation
 */

import { Timestamp } from "firebase/firestore";
import { ValuesVisibilityMode, WorkspacePermissions, WorkspaceRole, CustomRole } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseWorkspaceHelpersParams {
  isOwner: boolean;
  currentUserRole: WorkspaceRole;
  permissions: WorkspacePermissions | null;
  customRoles?: CustomRole[];
  customRoleNames?: Record<string, string>;
}

export interface UseWorkspaceHelpersReturn {
  formatDate: (timestamp: Timestamp | Date | undefined) => string;
  getRoleDisplayName: (role: string) => string;
  canManageWorkspaceConfig: () => boolean;
  canManageMembers: () => boolean;
  validateEmail: (email: string) => { valid: boolean; error?: string };
}

/**
 * Hook for workspace helper functions
 */
export function useWorkspaceHelpers({
  isOwner,
  currentUserRole,
  permissions,
  customRoles = [],
  customRoleNames = {},
}: UseWorkspaceHelpersParams): UseWorkspaceHelpersReturn {
  /**
   * Format a timestamp or date to a readable string
   */
  const formatDate = (timestamp: Timestamp | Date | undefined): string => {
    if (!timestamp) return "Unknown";
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  /**
   * Get display name for a role
   */
  const getRoleDisplayName = (role: string): string => {
    // Check if it's a custom role
    const customRole = customRoles.find((r) => r.id === role);
    if (customRole) {
      return customRole.name;
    }

    // Check if there's a custom name for this standard role
    if (customRoleNames[role]) {
      return customRoleNames[role];
    }

    // Standard roles with default names
    const roleMap: Record<string, string> = {
      owner: "Owner",
      admin: "Admin",
      user: "User",
      viewer: "Viewer",
      member: "Member", // Legacy role
    };
    return roleMap[role] || role;
  };

  /**
   * Check if current user can manage workspace configuration
   * In demo mode, return true to show UI elements (but actions will be blocked)
   */
  const canManageWorkspaceConfig = (): boolean => {
    // In demo mode, show UI elements but actions will be blocked
    if (isDemoMode()) return true;
    
    // If permissions haven't loaded yet, default to owner check
    if (!permissions) {
      return isOwner;
    }
    
    // Owner always has permission
    if (currentUserRole === "owner") return true;
    
    // Check if user's role is in the allowed roles
    const allowedRoles = permissions.manageWorkspaceConfig || ["owner", "admin"];
    return allowedRoles.includes(currentUserRole);
  };

  /**
   * Check if current user can add/remove members (owner always can; cannot remove owner)
   * In demo mode, return true to show UI elements (but actions will be blocked)
   */
  const canManageMembers = (): boolean => {
    if (isDemoMode()) return true;
    if (!permissions) return isOwner;
    if (currentUserRole === "owner") return true;
    const allowedRoles = permissions.manageMembers || ["owner", "admin"];
    return allowedRoles.includes(currentUserRole);
  };

  /**
   * Validate email address
   */
  const validateEmail = (email: string): { valid: boolean; error?: string } => {
    if (!email.trim()) {
      return { valid: false, error: "Email is required" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return { valid: false, error: "Please enter a valid email address" };
    }

    return { valid: true };
  };

  return {
    formatDate,
    getRoleDisplayName,
    canManageWorkspaceConfig,
    canManageMembers,
    validateEmail,
  };
}
